/**
 * Quiz Submissions Page
 * Displays all submissions/attempts for a specific quiz
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiUser, FiClock, FiCheckCircle, FiX, FiEye, FiCalendar, FiFilter, FiRefreshCw, FiTrash2, FiFileText, FiUsers, FiPlay, FiRotateCcw, FiPlus, FiEdit3, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import SuccessModal from '../../components/modals/SuccessModal';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--primary-color);
  background: var(--primary-color);
  color: var(--text-inverse);
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  box-shadow: 0 2px 8px rgba(216, 184, 137, 0.3);
  min-height: 44px;
  touch-action: manipulation;

  &:hover {
    background: var(--primary-hover);
    border-color: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(216, 184, 137, 0.4);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 0.875rem 1rem;
  }
`;

const Title = styled.h1`
  font-size: clamp(1.5rem, 2.5vw + 1rem, 2rem);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  text-align: center;
  flex: 1;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    text-align: left;
  }
  
  @media (max-width: 480px) {
    font-size: 1.375rem;
  }
`;

const QuizInfo = styled.div`
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const QuizTitle = styled.h2`
  font-size: clamp(1.25rem, 2vw + 1rem, 1.5rem);
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const QuizMeta = styled.div`
  display: flex;
  gap: 2rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SubmissionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SubmissionCard = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  transition: var(--transition);

  &:hover {
    border-color: var(--primary-color);
    box-shadow: var(--shadow-sm);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const SubmissionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const StudentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StudentName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const StudentEmail = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0;
`;

const SubmissionMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: right;
`;

const ScoreDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${props => props.correct ? 'var(--success-color)' : 'var(--danger-color)'};
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'completed': return 'var(--success-light)';
      case 'in_progress': return 'var(--warning-light)';
      case 'overdue': return 'var(--danger-light)';
      default: return 'var(--background-tertiary)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return 'var(--success-dark)';
      case 'in_progress': return 'var(--warning-dark)';
      case 'overdue': return 'var(--danger-dark)';
      default: return 'var(--text-secondary)';
    }
  }};
`;

const SubmissionDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--background-primary);
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);

  &:hover:not(:disabled) {
    background: var(--background-tertiary);
    border-color: var(--border-dark);
    color: var(--text-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.primary {
    background: var(--primary-color);
    border-color: var(--primary-color);
    color: var(--text-inverse);

    &:hover:not(:disabled) {
      background: var(--primary-hover);
      border-color: var(--primary-hover);
    }
  }

  &.danger {
    background: linear-gradient(135deg, var(--error-color) 0%, var(--error-dark) 100%);
    border-color: var(--error-color);
    color: var(--text-inverse);
    box-shadow: 0 2px 8px rgba(252, 129, 129, 0.3);

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, var(--error-dark) 0%, var(--error-color) 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(252, 129, 129, 0.4);
    }
  }

  &.warning {
    background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
    border-color: var(--primary-color);
    color: var(--text-inverse);
    box-shadow: 0 2px 8px rgba(216, 184, 137, 0.3);

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-color) 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(216, 184, 137, 0.4);
    }
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--danger-color);
`;

const ExtendTimerModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ExtendTimerModalContent = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-color);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ExtendTimerModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ExtendTimerModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
`;

const ExtendTimerModalBody = styled.div`
  padding: 1.5rem;
`;

const ExtendTimerModalFooter = styled.div`
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const ExtendTimerInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
  background: var(--background-primary);
  color: var(--text-primary);
  margin-top: 0.5rem;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const ExtendTimerLabel = styled.label`
  display: block;
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const ExtendTimerModal = ({ isOpen, onClose, onConfirm, studentName, minutes, onMinutesChange, isLoading }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <ExtendTimerModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ExtendTimerModalContent
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ExtendTimerModalHeader>
            <ExtendTimerModalTitle>Extend Timer</ExtendTimerModalTitle>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: 0,
                transition: 'var(--transition)'
              }}
            >
              <FiX size={18} />
            </button>
          </ExtendTimerModalHeader>

          <ExtendTimerModalBody>
            <p style={{ color: 'var(--text-primary)', marginBottom: '1.5rem' }}>
              Extend the timer for {studentName} by adding minutes to the current time limit.
            </p>
            <ExtendTimerLabel>
              Minutes to Add
            </ExtendTimerLabel>
            <ExtendTimerInput
              type="number"
              min="0.5"
              step="0.5"
              value={minutes}
              onChange={(e) => onMinutesChange(parseFloat(e.target.value) || 0)}
              placeholder="Enter minutes"
              disabled={isLoading}
            />
          </ExtendTimerModalBody>

          <ExtendTimerModalFooter>
            <ActionButton
              className="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </ActionButton>
            <ActionButton
              className="primary"
              onClick={onConfirm}
              disabled={isLoading || !minutes || minutes <= 0}
            >
              {isLoading ? 'Extending...' : 'Extend Timer'}
            </ActionButton>
          </ExtendTimerModalFooter>
        </ExtendTimerModalContent>
      </ExtendTimerModalOverlay>
    </AnimatePresence>
  );
};

const PartialRetakeModal = ({ isOpen, onClose, onConfirm, studentName, questions, timeRemaining, timeLimitMinutes, selectedQuestionNumbers, onQuestionNumbersChange, timerOption, onTimerOptionChange, extendMinutes, onExtendMinutesChange, attemptsAllowed, isLoading, questionsWithResponses }) => {
  if (!isOpen) return null;

  const toggleQuestion = (questionNumber) => {
    // Allow toggling any question - instructor has full control
    const newSelection = selectedQuestionNumbers.includes(questionNumber)
      ? selectedQuestionNumbers.filter(n => n !== questionNumber)
      : [...selectedQuestionNumbers, questionNumber].sort((a, b) => a - b);
    onQuestionNumbersChange(newSelection);
  };
  
  const selectAll = () => {
    if (questions && questions.length > 0) {
      // Select all questions - instructor decides which ones
      const allNumbers = questions.map(q => q.order_index).sort((a, b) => a - b);
      onQuestionNumbersChange(allNumbers);
    }
  };


  const deselectAll = () => {
    onQuestionNumbersChange([]);
  };

  return (
    <AnimatePresence>
      <ExtendTimerModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ExtendTimerModalContent
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '700px' }}
        >
          <ExtendTimerModalHeader>
            <ExtendTimerModalTitle>Grant Partial Retake</ExtendTimerModalTitle>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: 0,
                transition: 'var(--transition)'
              }}
            >
              <FiX size={18} />
            </button>
          </ExtendTimerModalHeader>

          <ExtendTimerModalBody>
            <p style={{ color: 'var(--text-primary)', marginBottom: '1.5rem' }}>
              Allow <strong>{studentName}</strong> to retake specific questions. Selected questions will be cleared and the student can answer them again.
            </p>

            {attemptsAllowed === 1 && (
              <div style={{
                background: 'rgba(246, 224, 94, 0.1)',
                border: `1px solid var(--warning-color)`,
                borderRadius: 0,
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{ color: 'var(--warning-dark)', margin: 0, fontSize: '0.875rem' }}>
                  <strong>Note:</strong> This is a single-attempt quiz. The attempt will be reset to allow the student to retake the selected questions.
                </p>
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <ExtendTimerLabel style={{ margin: 0 }}>
                  Select Questions to Retake
                </ExtendTimerLabel>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={selectAll}
                    style={{
                      padding: '0.25rem 0.75rem',
                      fontSize: '0.75rem',
                      border: '1px solid var(--border-color)',
                      borderRadius: 0,
                      background: 'var(--background-primary)',
                      color: 'var(--text-primary)',
                      cursor: 'pointer'
                    }}
                  >
                    Select All
                  </button>
                  <button
                    onClick={deselectAll}
                    style={{
                      padding: '0.25rem 0.75rem',
                      fontSize: '0.75rem',
                      border: '1px solid var(--border-color)',
                      borderRadius: 0,
                      background: 'var(--background-primary)',
                      color: 'var(--text-primary)',
                      cursor: 'pointer'
                    }}
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <p style={{ 
                color: 'var(--text-secondary)', 
                fontSize: '0.75rem', 
                marginBottom: '0.75rem',
                fontStyle: 'italic'
              }}>
                Select any questions you want the student to retake. Questions that were never answered can also be selected to allow the student to answer them.
              </p>
              <div style={{
                maxHeight: '300px',
                overflowY: 'auto',
                border: '1px solid var(--border-color)',
                borderRadius: 0,
                padding: '0.5rem'
              }}>
                {questions && questions.length > 0 ? (
                  questions
                    .sort((a, b) => a.order_index - b.order_index)
                    .map(question => {
                      const isSelected = selectedQuestionNumbers.includes(question.order_index);
                      const hasResponse = questionsWithResponses.has(question.id);
                      
                      return (
                        <label
                          key={question.id}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.75rem',
                            padding: '0.75rem',
                            borderRadius: 0,
                            border: isSelected ? '2px solid var(--primary-color)' : '1px solid var(--border-color)',
                            cursor: 'pointer',
                            marginBottom: '0.5rem',
                            background: isSelected 
                              ? 'rgba(216, 184, 137, 0.1)' 
                              : 'transparent',
                            transition: 'var(--transition)'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = 'var(--background-tertiary)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = 'transparent';
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleQuestion(question.order_index)}
                            style={{ 
                              cursor: 'pointer', 
                              marginTop: '0.25rem' 
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                              <strong style={{ color: 'var(--text-primary)' }}>
                                Q{question.order_index}
                              </strong>
                              <span style={{
                                fontSize: '0.75rem',
                                padding: '0.125rem 0.5rem',
                                background: 'var(--text-muted)',
                                color: 'var(--text-inverse)',
                                borderRadius: 0,
                                textTransform: 'uppercase'
                              }}>
                                {question.type}
                              </span>
                              {!hasResponse && (
                                <span style={{
                                  fontSize: '0.7rem',
                                  padding: '0.125rem 0.5rem',
                                  background: 'var(--warning-color)',
                                  color: 'var(--text-inverse)',
                                  borderRadius: 0
                                }}>
                                  Not Answered
                                </span>
                              )}
                            </div>
                            <div style={{
                              color: 'var(--text-secondary)',
                              fontSize: '0.875rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {question.prompt || 'No prompt'}
                            </div>
                          </div>
                        </label>
                      );
                    })
                ) : (
                  <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '1rem' }}>
                    No questions available
                  </p>
                )}
              </div>
              {selectedQuestionNumbers.length > 0 && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  {selectedQuestionNumbers.length} question(s) selected: {selectedQuestionNumbers.join(', ')}
                </p>
              )}
            </div>

            {timeLimitMinutes && (
              <>
                {timeRemaining !== null && (
                  <div style={{
                    background: 'var(--background-secondary)',
                    padding: '1rem',
                    borderRadius: 0,
                    marginBottom: '1.5rem',
                    border: '1px solid var(--border-color)'
                  }}>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                      Current Time Remaining:
                    </p>
                    <p style={{
                      color: timeRemaining <= 300 ? 'var(--error-color)' : timeRemaining <= 600 ? 'var(--warning-color)' : 'var(--text-primary)',
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      margin: 0
                    }}>
                      {quizApiService.formatTimeRemaining(timeRemaining)}
                    </p>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.5rem', margin: 0 }}>
                      Full time limit: {timeLimitMinutes} minutes
                    </p>
                  </div>
                )}
                {timeRemaining === null && (
                  <div style={{
                    background: 'var(--background-secondary)',
                    padding: '1rem',
                    borderRadius: 0,
                    marginBottom: '1.5rem',
                    border: '1px solid var(--border-color)'
                  }}>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                      Full time limit: {timeLimitMinutes} minutes
                    </p>
                  </div>
                )}
                <ExtendTimerLabel style={{ marginBottom: '1rem' }}>
                  Timer Options
                </ExtendTimerLabel>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    borderRadius: 0,
                    border: '1px solid var(--border-color)',
                    cursor: 'pointer',
                    marginBottom: '0.75rem',
                    transition: 'var(--transition)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background-tertiary)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <input
                      type="radio"
                      name="partialRetakeTimerOption"
                      value="keep"
                      checked={timerOption === 'keep'}
                      onChange={(e) => onTimerOptionChange(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ color: 'var(--text-primary)' }}>
                      Keep original time {timeRemaining !== null ? `(${quizApiService.formatTimeRemaining(timeRemaining)} remaining)` : '(use current timer state)'}
                    </span>
                  </label>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    borderRadius: 0,
                    border: '1px solid var(--border-color)',
                    cursor: 'pointer',
                    marginBottom: '0.75rem',
                    transition: 'var(--transition)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background-tertiary)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <input
                      type="radio"
                      name="partialRetakeTimerOption"
                      value="reset"
                      checked={timerOption === 'reset'}
                      onChange={(e) => onTimerOptionChange(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ color: 'var(--text-primary)' }}>
                      Reset timer to full time limit ({timeLimitMinutes ? `${timeLimitMinutes} minutes` : 'full time'})
                    </span>
                  </label>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    borderRadius: 0,
                    border: '1px solid var(--border-color)',
                    cursor: 'pointer',
                    transition: 'var(--transition)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background-tertiary)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <input
                      type="radio"
                      name="partialRetakeTimerOption"
                      value="extend"
                      checked={timerOption === 'extend'}
                      onChange={(e) => {
                        onTimerOptionChange(e.target.value);
                        if (e.target.checked) {
                          setTimeout(() => {
                            const input = document.getElementById('partial-retake-extend-minutes');
                            if (input) input.focus();
                          }, 100);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ color: 'var(--text-primary)', flex: 1 }}>
                      Extend timer by:
                    </span>
                    <ExtendTimerInput
                      id="partial-retake-extend-minutes"
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={extendMinutes}
                      onChange={(e) => onExtendMinutesChange(parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      disabled={isLoading || timerOption !== 'extend'}
                      style={{ width: '100px', margin: 0 }}
                    />
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                      minutes
                    </span>
                  </label>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    borderRadius: 0,
                    border: '1px solid var(--border-color)',
                    cursor: 'pointer',
                    transition: 'var(--transition)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background-tertiary)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <input
                      type="radio"
                      name="partialRetakeTimerOption"
                      value="disable"
                      checked={timerOption === 'disable'}
                      onChange={(e) => onTimerOptionChange(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ color: 'var(--text-primary)' }}>
                      Disable timer (no time limit)
                    </span>
                  </label>
                </div>
              </>
            )}
          </ExtendTimerModalBody>

          <ExtendTimerModalFooter>
            <ActionButton
              className="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </ActionButton>
            <ActionButton
              className="primary"
              onClick={onConfirm}
              disabled={isLoading || selectedQuestionNumbers.length === 0 || (timerOption === 'extend' && (!extendMinutes || extendMinutes <= 0))}
            >
              {isLoading ? 'Granting...' : 'Grant Partial Retake'}
            </ActionButton>
          </ExtendTimerModalFooter>
        </ExtendTimerModalContent>
      </ExtendTimerModalOverlay>
    </AnimatePresence>
  );
};

const ResumeTimerModal = ({ isOpen, onClose, onConfirm, studentName, timeRemaining, timeLimitMinutes, extendMinutes, onExtendMinutesChange, isLoading }) => {
  const [selectedOption, setSelectedOption] = useState('keep');
  
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <ExtendTimerModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ExtendTimerModalContent
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ExtendTimerModalHeader>
            <ExtendTimerModalTitle>Resume Quiz - Timer Options</ExtendTimerModalTitle>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: 0,
                transition: 'var(--transition)'
              }}
            >
              <FiX size={18} />
            </button>
          </ExtendTimerModalHeader>

          <ExtendTimerModalBody>
            <p style={{ color: 'var(--text-primary)', marginBottom: '1rem' }}>
              Resuming quiz for <strong>{studentName}</strong>
            </p>
            <div style={{ 
              background: 'var(--background-secondary)', 
              padding: '1rem', 
              borderRadius: 0,
              marginBottom: '1.5rem',
              border: '1px solid var(--border-color)'
            }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                Current Time Remaining:
              </p>
              <p style={{ 
                color: timeRemaining <= 300 ? 'var(--error-color)' : timeRemaining <= 600 ? 'var(--warning-color)' : 'var(--text-primary)', 
                fontSize: '1.25rem', 
                fontWeight: '600',
                margin: 0
              }}>
                {quizApiService.formatTimeRemaining(timeRemaining)}
              </p>
              {timeLimitMinutes && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.5rem', margin: 0 }}>
                  Full time limit: {timeLimitMinutes} minutes
                </p>
              )}
            </div>
            <p style={{ color: 'var(--text-primary)', marginBottom: '1rem' }}>
              Choose an option:
            </p>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                padding: '0.75rem',
                borderRadius: 0,
                border: '1px solid var(--border-color)',
                cursor: 'pointer',
                marginBottom: '0.75rem',
                transition: 'var(--transition)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <input
                  type="radio"
                  name="resumeOption"
                  value="keep"
                  checked={selectedOption === 'keep'}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ color: 'var(--text-primary)' }}>
                  Keep original time ({quizApiService.formatTimeRemaining(timeRemaining)} remaining)
                </span>
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                padding: '0.75rem',
                borderRadius: 0,
                border: '1px solid var(--border-color)',
                cursor: 'pointer',
                marginBottom: '0.75rem',
                transition: 'var(--transition)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <input
                  type="radio"
                  name="resumeOption"
                  value="reset"
                  checked={selectedOption === 'reset'}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ color: 'var(--text-primary)' }}>
                  Reset timer to full time limit ({timeLimitMinutes ? `${timeLimitMinutes} minutes` : 'full time'})
                </span>
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                padding: '0.75rem',
                borderRadius: 0,
                border: '1px solid var(--border-color)',
                cursor: 'pointer',
                marginBottom: '0.75rem',
                transition: 'var(--transition)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <input
                  type="radio"
                  name="resumeOption"
                  value="extend"
                  checked={selectedOption === 'extend'}
                  onChange={(e) => {
                    setSelectedOption(e.target.value);
                    if (e.target.checked) {
                      // Focus on input when extend is selected
                      setTimeout(() => {
                        const input = document.getElementById('resume-extend-minutes');
                        if (input) input.focus();
                      }, 100);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ color: 'var(--text-primary)', flex: 1 }}>
                  Extend timer by:
                </span>
                <ExtendTimerInput
                  id="resume-extend-minutes"
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={extendMinutes}
                  onChange={(e) => onExtendMinutesChange(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  disabled={isLoading}
                  style={{ width: '100px', margin: 0 }}
                />
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  minutes
                </span>
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                padding: '0.75rem',
                borderRadius: 0,
                border: '1px solid var(--border-color)',
                cursor: 'pointer',
                transition: 'var(--transition)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <input
                  type="radio"
                  name="resumeOption"
                  value="disable"
                  checked={selectedOption === 'disable'}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ color: 'var(--text-primary)' }}>
                  Disable timer (no time limit)
                </span>
              </label>
            </div>
          </ExtendTimerModalBody>

          <ExtendTimerModalFooter>
            <ActionButton
              className="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </ActionButton>
            <ActionButton
              className="primary"
              onClick={() => onConfirm(selectedOption)}
              disabled={isLoading || (selectedOption === 'extend' && (!extendMinutes || extendMinutes <= 0))}
            >
              {isLoading ? 'Resuming...' : 'Resume Quiz'}
            </ActionButton>
          </ExtendTimerModalFooter>
        </ExtendTimerModalContent>
      </ExtendTimerModalOverlay>
    </AnimatePresence>
  );
};

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  background: var(--background-primary);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const SearchResults = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-family: 'Inter', sans-serif;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
  
  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    justify-content: flex-start;
  }
`;

const FilterLabel = styled.span`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: ${props => props.active ? 'var(--primary-color)' : 'var(--background-primary)'};
  color: ${props => props.active ? 'white' : 'var(--text-secondary)'};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;

  &:hover {
    background: ${props => props.active ? 'var(--primary-hover)' : 'var(--background-tertiary)'};
    border-color: ${props => props.active ? 'var(--primary-hover)' : 'var(--border-dark)'};
    color: ${props => props.active ? 'white' : 'var(--text-primary)'};
  }
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

function QuizSubmissions() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeLimit, setTimeLimit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'started', 'in_progress', 'not_started', 'incomplete'
  const [sortOrder, setSortOrder] = useState('none'); // 'none', 'score-low-to-high', 'score-high-to-low'
  const [incompleteSubmissions, setIncompleteSubmissions] = useState(new Set()); // Set of assignment_ids with incomplete questions
  const [checkingIncomplete, setCheckingIncomplete] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [showResetTimerModal, setShowResetTimerModal] = useState(false);
  const [showExtendTimerModal, setShowExtendTimerModal] = useState(false);
  const [showResumeTimerModal, setShowResumeTimerModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [isResettingTimer, setIsResettingTimer] = useState(false);
  const [isExtendingTimer, setIsExtendingTimer] = useState(false);
  const [extendMinutes, setExtendMinutes] = useState(10);
  const [timeRemainingOnResume, setTimeRemainingOnResume] = useState(null);
  const [resumeExtendMinutes, setResumeExtendMinutes] = useState(0);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPartialRetakeModal, setShowPartialRetakeModal] = useState(false);
  const [isGrantingPartialRetake, setIsGrantingPartialRetake] = useState(false);
  const [selectedQuestionNumbers, setSelectedQuestionNumbers] = useState([]);
  const [partialRetakeTimerOption, setPartialRetakeTimerOption] = useState('keep');
  const [partialRetakeExtendMinutes, setPartialRetakeExtendMinutes] = useState(0);
  const [questionsWithResponses, setQuestionsWithResponses] = useState(new Set());

  const fetchQuizSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch quiz details
      const quizResponse = await quizApiService.getQuiz(quizId);
      if (quizResponse.success) {
        setQuiz(quizResponse.data);
      }

      // Fetch submissions for this quiz
      const submissionsResponse = await quizApiService.getQuizAttempts(quizId);
      if (submissionsResponse.success) {
        setSubmissions(submissionsResponse.data);
        // Extract time limit from the first submission (all should have the same time limit)
        if (submissionsResponse.data.length > 0 && submissionsResponse.data[0].time_limit_minutes) {
          setTimeLimit(submissionsResponse.data[0].time_limit_minutes);
        }
      } else {
        setError(submissionsResponse.message || 'Failed to load submissions');
      }
    } catch (err) {
      console.error('Error fetching quiz submissions:', err);
      setError('Failed to load quiz submissions');
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  useEffect(() => {
    if (quizId) {
      fetchQuizSubmissions();
    }
  }, [quizId, fetchQuizSubmissions]);

  const checkIncompleteSubmissions = useCallback(async () => {
    if (!quiz || !quiz.questions || checkingIncomplete) return;
    
    setCheckingIncomplete(true);
    const incompleteSet = new Set();
    const totalQuestions = quiz.questions.length;

    // Check each submission that has been started
    for (const submission of submissions) {
      if (!submission.attempt_id) continue; // Skip if not started
      
      try {
        const attemptResponse = await quizApiService.getAttemptDetails(submission.attempt_id);
        if (attemptResponse.success && attemptResponse.data) {
          // Count responses
          let responseCount = 0;
          
          // Check responses array
          if (attemptResponse.data.responses && Array.isArray(attemptResponse.data.responses)) {
            responseCount = attemptResponse.data.responses.length;
          }
          
          // Also check feedback structure
          if (attemptResponse.data.feedback && Array.isArray(attemptResponse.data.feedback)) {
            // Count unique question IDs with responses
            const questionsWithResponses = new Set();
            attemptResponse.data.feedback.forEach(f => {
              if (f.question_id && (f.response_data !== null && f.response_data !== undefined)) {
                questionsWithResponses.add(f.question_id);
              }
            });
            // Use the larger count
            responseCount = Math.max(responseCount, questionsWithResponses.size);
          }
          
          // If responses are less than total questions, it's incomplete
          if (responseCount < totalQuestions) {
            incompleteSet.add(submission.assignment_id);
          }
        }
      } catch (error) {
        console.error(`Error checking incomplete status for attempt ${submission.attempt_id}:`, error);
        // Continue checking other submissions even if one fails
      }
    }
    
    setIncompleteSubmissions(incompleteSet);
    setCheckingIncomplete(false);
  }, [quiz, submissions, checkingIncomplete]);

  // Check for incomplete submissions when quiz and submissions are loaded
  useEffect(() => {
    if (quiz && quiz.questions && submissions.length > 0 && statusFilter === 'incomplete') {
      checkIncompleteSubmissions();
    }
  }, [quiz, submissions, statusFilter, checkIncompleteSubmissions]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (submission) => {
    if (submission.submitted_at) {
      return 'completed';
    } else if (submission.started_at) {
      return 'in_progress';
    } else {
      return 'not_started';
    }
  };

  const handleViewSubmission = (submission) => {
    // Navigate to detailed submission view using attempt_id
    if (submission.attempt_id) {
      navigate(`/instructor/submissions/${submission.attempt_id}`);
    }
  };

  const handleResetClick = (submission) => {
    setSelectedAssignment(submission);
    setShowResetModal(true);
  };

  const handleResumeClick = (submission) => {
    setSelectedAssignment(submission);
    
    // Check if it's a timed quiz
    if (submission.time_limit_minutes && submission.started_at) {
      // Calculate time remaining (even if submitted, we want to show the timer options)
      const timeLimitSeconds = submission.time_limit_minutes * 60;
      const startedAt = new Date(submission.started_at);
      const now = new Date();
      const elapsed = Math.floor((now - startedAt) / 1000);
      const remaining = timeLimitSeconds - elapsed;
      
      // Show timer modal with time remaining (even if time expired or quiz is submitted)
      // This allows instructor to reset or extend timer when resuming
      setTimeRemainingOnResume(Math.max(0, remaining)); // Ensure non-negative
      setResumeExtendMinutes(0);
      setShowResumeTimerModal(true);
    } else {
      // Not a timed quiz or no time limit, show regular resume modal
      setShowResumeModal(true);
    }
  };

  const handleUnassignClick = (submission) => {
    setSelectedAssignment(submission);
    setShowUnassignModal(true);
  };

  const handleResetTimerClick = (submission) => {
    setSelectedAssignment(submission);
    setShowResetTimerModal(true);
  };

  const handleExtendTimerClick = (submission) => {
    setSelectedAssignment(submission);
    setExtendMinutes(10);
    setShowExtendTimerModal(true);
  };

  const handlePartialRetakeClick = async (submission) => {
    setSelectedAssignment(submission);
    setSelectedQuestionNumbers([]);
    setPartialRetakeTimerOption('keep');
    setPartialRetakeExtendMinutes(0);
    
    // Fetch attempt details to see which questions have responses (optional - for UI display only)
    if (submission.attempt_id) {
      try {
        const attemptResponse = await quizApiService.getAttemptDetails(submission.attempt_id);
        if (attemptResponse.success && attemptResponse.data) {
          // Get responses from the API response
          let responses = [];
          let questionIdsWithResponses = new Set();
          
          // Check if responses are directly in data.responses
          if (attemptResponse.data.responses && Array.isArray(attemptResponse.data.responses)) {
            responses = attemptResponse.data.responses;
            // Extract question IDs from responses
            responses.forEach(r => {
              if (r.question_id) {
                questionIdsWithResponses.add(parseInt(r.question_id));
              }
            });
          }
          
          // Also check feedback structure which contains response_data with question_id
          if (attemptResponse.data.feedback && Array.isArray(attemptResponse.data.feedback)) {
            attemptResponse.data.feedback.forEach(f => {
              // Each feedback item has question_id
              if (f.question_id) {
                questionIdsWithResponses.add(parseInt(f.question_id));
              }
              // Also check if response_data exists (indicates a response was made)
              if (f.response_data !== null && f.response_data !== undefined) {
                if (f.question_id) {
                  questionIdsWithResponses.add(parseInt(f.question_id));
                }
              }
            });
          }
          
          if (questionIdsWithResponses.size > 0) {
            setQuestionsWithResponses(questionIdsWithResponses);
            console.log('Questions with responses:', Array.from(questionIdsWithResponses));
          } else {
            console.log('No responses found in attempt details');
            setQuestionsWithResponses(new Set());
          }
        } else {
          setQuestionsWithResponses(new Set());
        }
      } catch (error) {
        console.error('Error fetching attempt details (non-critical):', error);
        // Non-critical error - just proceed without response info
        setQuestionsWithResponses(new Set());
      }
    } else {
      setQuestionsWithResponses(new Set());
    }
    
    setShowPartialRetakeModal(true);
  };

  const handleConfirmReset = async () => {
    if (!selectedAssignment) return;

    setIsResetting(true);
    try {
      const response = await quizApiService.resetStudentQuizAttempts(
        quizId,
        [selectedAssignment.assignment_id]
      );

      if (response.success) {
        setSuccessMessage(`Successfully reset quiz attempt for ${selectedAssignment.student_name}.`);
        setSuccessModalOpen(true);
        setShowResetModal(false);
        setSelectedAssignment(null);
        // Refresh submissions
        await fetchQuizSubmissions();
      } else {
        throw new Error(response.message || 'Failed to reset quiz attempt');
      }
    } catch (error) {
      console.error('Error resetting quiz attempt:', error);
      setErrorMessage(error.message || 'Failed to reset quiz attempt. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsResetting(false);
    }
  };

  const handleConfirmResume = async () => {
    if (!selectedAssignment) return;

    setIsResuming(true);
    try {
      const response = await quizApiService.resumeStudentQuizAttempts(
        quizId,
        [selectedAssignment.assignment_id]
      );

      if (response.success) {
        const answersPreserved = response.data?.answersPreserved || 0;
        setSuccessMessage(
          `Successfully enabled resume for ${selectedAssignment.student_name}. ` +
          `${answersPreserved} answer(s) preserved.`
        );
        setSuccessModalOpen(true);
        setShowResumeModal(false);
        setSelectedAssignment(null);
        // Refresh submissions
        await fetchQuizSubmissions();
      } else {
        throw new Error(response.message || 'Failed to resume quiz attempt');
      }
    } catch (error) {
      console.error('Error resuming quiz attempt:', error);
      setErrorMessage(error.message || 'Failed to resume quiz attempt. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsResuming(false);
    }
  };

  const handleConfirmResumeWithTimer = async (option) => {
    if (!selectedAssignment) return;

    setIsResuming(true);
    try {
      // First resume the quiz
      const resumeResponse = await quizApiService.resumeStudentQuizAttempts(
        quizId,
        [selectedAssignment.assignment_id]
      );

      if (!resumeResponse.success) {
        throw new Error(resumeResponse.message || 'Failed to resume quiz attempt');
      }

      const answersPreserved = resumeResponse.data?.answersPreserved || 0;
      let successMsg = '';

      // Handle different timer options
      if (option === 'extend' && resumeExtendMinutes > 0) {
        // Extend the timer
        const extendResponse = await quizApiService.extendQuizTimer(
          quizId,
          [selectedAssignment.assignment_id],
          resumeExtendMinutes
        );

        if (!extendResponse.success) {
          throw new Error(extendResponse.message || 'Failed to extend timer');
        }

        successMsg = `Successfully enabled resume for ${selectedAssignment.student_name} and extended timer by ${resumeExtendMinutes} minute(s). ` +
          `${answersPreserved} answer(s) preserved.`;
      } else if (option === 'reset') {
        // Reset the timer to full time limit
        const resetResponse = await quizApiService.resetQuizTimer(
          quizId,
          [selectedAssignment.assignment_id]
        );

        if (!resetResponse.success) {
          throw new Error(resetResponse.message || 'Failed to reset timer');
        }

        successMsg = `Successfully enabled resume for ${selectedAssignment.student_name} and reset timer to full time limit. ` +
          `${answersPreserved} answer(s) preserved.`;
      } else if (option === 'disable') {
        // Disable the timer
        const disableResponse = await quizApiService.disableQuizTimer(
          quizId,
          [selectedAssignment.assignment_id]
        );

        if (!disableResponse.success) {
          throw new Error(disableResponse.message || 'Failed to disable timer');
        }

        successMsg = `Successfully enabled resume for ${selectedAssignment.student_name} and disabled timer (no time limit). ` +
          `${answersPreserved} answer(s) preserved.`;
      } else {
        // Keep original time
        successMsg = `Successfully enabled resume for ${selectedAssignment.student_name}. ` +
          `${answersPreserved} answer(s) preserved. Time remaining: ${quizApiService.formatTimeRemaining(timeRemainingOnResume)}.`;
      }

      setSuccessMessage(successMsg);
      setSuccessModalOpen(true);
      setShowResumeTimerModal(false);
      setSelectedAssignment(null);
      setTimeRemainingOnResume(null);
      setResumeExtendMinutes(0);
      // Refresh submissions
      await fetchQuizSubmissions();
    } catch (error) {
      console.error('Error resuming quiz attempt:', error);
      setErrorMessage(error.message || 'Failed to resume quiz attempt. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsResuming(false);
    }
  };

  const handleConfirmResetTimer = async () => {
    if (!selectedAssignment) return;

    setIsResettingTimer(true);
    try {
      const response = await quizApiService.resetQuizTimer(
        quizId,
        [selectedAssignment.assignment_id]
      );

      if (response.success) {
        setSuccessMessage(
          `Successfully reset timer for ${selectedAssignment.student_name}. ` +
          `The timer has been reset to the full time limit.`
        );
        setSuccessModalOpen(true);
        setShowResetTimerModal(false);
        setSelectedAssignment(null);
        // Refresh submissions
        await fetchQuizSubmissions();
      } else {
        throw new Error(response.message || 'Failed to reset timer');
      }
    } catch (error) {
      console.error('Error resetting timer:', error);
      setErrorMessage(error.message || 'Failed to reset timer. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsResettingTimer(false);
    }
  };

  const handleConfirmExtendTimer = async () => {
    if (!selectedAssignment || !extendMinutes || extendMinutes <= 0) return;

    setIsExtendingTimer(true);
    try {
      const response = await quizApiService.extendQuizTimer(
        quizId,
        [selectedAssignment.assignment_id],
        extendMinutes
      );

      if (response.success) {
        setSuccessMessage(
          `Successfully extended timer by ${extendMinutes} minute(s) for ${selectedAssignment.student_name}.`
        );
        setSuccessModalOpen(true);
        setShowExtendTimerModal(false);
        setSelectedAssignment(null);
        setExtendMinutes(10);
        // Refresh submissions
        await fetchQuizSubmissions();
      } else {
        throw new Error(response.message || 'Failed to extend timer');
      }
    } catch (error) {
      console.error('Error extending timer:', error);
      setErrorMessage(error.message || 'Failed to extend timer. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsExtendingTimer(false);
    }
  };

  const handleConfirmUnassign = async () => {
    if (!selectedAssignment) return;

    setIsUnassigning(true);
    try {
      const result = await quizApiService.unassignMultipleQuizzes([selectedAssignment.assignment_id]);

      if (result.success || result.data?.successful > 0) {
        setSuccessMessage(`Successfully unassigned quiz for ${selectedAssignment.student_name}.`);
        setSuccessModalOpen(true);
        setShowUnassignModal(false);
        setSelectedAssignment(null);
        // Refresh submissions
        await fetchQuizSubmissions();
      } else {
        throw new Error(result.message || 'Failed to unassign quiz');
      }
    } catch (error) {
      console.error('Error unassigning quiz:', error);
      setErrorMessage(error.message || 'Failed to unassign quiz. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsUnassigning(false);
    }
  };

  const handleConfirmPartialRetake = async () => {
    if (!selectedAssignment || selectedQuestionNumbers.length === 0) return;

    setIsGrantingPartialRetake(true);
    try {
      // Calculate time remaining if timed quiz
      let timeRemaining = null;
      if (selectedAssignment.time_limit_minutes && selectedAssignment.started_at) {
        const timeLimitSeconds = selectedAssignment.time_limit_minutes * 60;
        const startedAt = new Date(selectedAssignment.started_at);
        const now = new Date();
        const elapsed = Math.floor((now - startedAt) / 1000);
        timeRemaining = Math.max(0, timeLimitSeconds - elapsed);
      }

      const response = await quizApiService.grantPartialRetake(
        quizId,
        selectedAssignment.assignment_id,
        {
          questionNumbers: selectedQuestionNumbers,
          timerOption: partialRetakeTimerOption,
          extendMinutes: partialRetakeTimerOption === 'extend' ? partialRetakeExtendMinutes : null,
          timeRemaining: timeRemaining
        }
      );

      if (response.success) {
        let successMsg = response.data.message || `Successfully granted partial retake for ${selectedAssignment.student_name}.`;
        
        if (selectedAssignment.attempts_allowed === 1) {
          successMsg += '\n\nSingle attempt has been reset.';
        }
        
        // Show score change if applicable
        if (response.data.score) {
          const before = response.data.score.before;
          const after = response.data.score.after;
          if (before.score !== after.score) {
            successMsg += `\n\nScore updated: ${before.score}/${before.maxScore} → ${after.score}/${after.maxScore}`;
          }
        }

        setSuccessMessage(successMsg);
        setSuccessModalOpen(true);
        setShowPartialRetakeModal(false);
        setSelectedAssignment(null);
        setSelectedQuestionNumbers([]);
        setPartialRetakeTimerOption('keep');
        setPartialRetakeExtendMinutes(0);
        // Refresh submissions
        await fetchQuizSubmissions();
      } else {
        throw new Error(response.message || 'Failed to grant partial retake');
      }
    } catch (error) {
      console.error('Error granting partial retake:', error);
      setErrorMessage(error.message || 'Failed to grant partial retake. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsGrantingPartialRetake(false);
    }
  };

  const getFilteredSubmissions = () => {
    let filtered = submissions;
    
    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'started') {
        // Completed: submitted submissions
        filtered = filtered.filter(submission => submission.submitted_at !== null && submission.submitted_at !== undefined);
      } else if (statusFilter === 'in_progress') {
        // In progress: started but not submitted
        filtered = filtered.filter(submission => 
          submission.started_at !== null && 
          submission.started_at !== undefined && 
          (!submission.submitted_at || submission.submitted_at === null)
        );
      } else if (statusFilter === 'not_started') {
        filtered = filtered.filter(submission => !submission.started_at || submission.started_at === null);
      } else if (statusFilter === 'incomplete') {
        // Incomplete: submissions with missing answers
        filtered = filtered.filter(submission => 
          incompleteSubmissions.has(submission.assignment_id)
        );
      }
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(submission => 
        submission.student_name.toLowerCase().includes(term) ||
        submission.student_email.toLowerCase().includes(term)
      );
    }
    
    // Apply score sorting
    if (sortOrder !== 'none') {
      filtered = [...filtered].sort((a, b) => {
        // Calculate score percentage for comparison
        const getScorePercentage = (submission) => {
          if (!submission.score && submission.score !== 0) return -1; // No score yet
          const score = parseFloat(submission.score) || 0;
          const maxScore = parseFloat(submission.max_score) || 1;
          return maxScore > 0 ? (score / maxScore) * 100 : 0;
        };
        
        const scoreA = getScorePercentage(a);
        const scoreB = getScorePercentage(b);
        
        // Submissions without scores go to the end
        if (scoreA === -1 && scoreB === -1) return 0;
        if (scoreA === -1) return 1;
        if (scoreB === -1) return -1;
        
        if (sortOrder === 'score-low-to-high') {
          return scoreA - scoreB;
        } else if (sortOrder === 'score-high-to-low') {
          return scoreB - scoreA;
        }
        return 0;
      });
    }
    
    return filtered;
  };

  // Calculate stats for submissions
  const getStats = () => {
    const completedSubmissions = submissions.filter(
      submission => submission.submitted_at !== null && 
                   submission.submitted_at !== undefined &&
                   submission.score !== null &&
                   submission.score !== undefined &&
                   submission.max_score !== null &&
                   submission.max_score !== undefined &&
                   submission.max_score > 0
    );

    const notStartedSubmissions = submissions.filter(
      submission => !submission.started_at || submission.started_at === null
    );

    const stats = {
      completed: completedSubmissions.length,
      notStarted: notStartedSubmissions.length
    };

    // Only calculate score stats if there are completed submissions
    if (completedSubmissions.length > 0) {
    const scores = completedSubmissions.map(s => {
      const score = parseFloat(s.score) || 0;
      const maxScore = parseFloat(s.max_score) || 1;
      return (score / maxScore) * 100; // Convert to percentage
    });

      stats.average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      stats.lowest = Math.min(...scores);
      stats.highest = Math.max(...scores);
    }

    return stats;
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>
          <FiClock size={48} />
          <h3>Loading submissions...</h3>
          <p>Please wait while we fetch the quiz submissions.</p>
        </LoadingState>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>
          <BackButton onClick={() => navigate('/instructor/quizzes')}>
            <FiArrowLeft size={16} />
            Back to Quizzes
          </BackButton>
        </Header>
        <ErrorState>
          <FiX size={48} />
          <h3>Error loading submissions</h3>
          <p>{error}</p>
        </ErrorState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/instructor/quizzes')}>
          <FiArrowLeft size={16} />
          Back to Quizzes
        </BackButton>
        <Title>Quiz Submissions</Title>
      </Header>

      {quiz && (
        <QuizInfo>
          <QuizTitle>{quiz.title}</QuizTitle>
          <QuizMeta>
            <MetaItem>
              <FiCalendar size={16} />
              Created: {formatDate(quiz.created_at)}
            </MetaItem>
            {timeLimit && (
              <MetaItem>
                <FiClock size={16} />
                Time Limit: {timeLimit} minutes
              </MetaItem>
            )}
            {quiz.questions && (
              <MetaItem>
                <FiFileText size={16} />
                Questions: {quiz.questions.length}
              </MetaItem>
            )}
            <MetaItem>
              <FiUsers size={16} />
              Assignments: {submissions.length}
            </MetaItem>
          </QuizMeta>
        </QuizInfo>
      )}

      {(() => {
        const stats = getStats();
        return stats && (
          <StatsContainer>
            <StatCard>
              <StatLabel>Completed Submissions</StatLabel>
              <StatValue>{stats.completed}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Pending Submissions</StatLabel>
              <StatValue>{stats.notStarted}</StatValue>
            </StatCard>
            {stats.average !== undefined && (
              <>
            <StatCard>
              <StatLabel>Average Score</StatLabel>
                  <StatValue>{stats.average.toFixed(1)}%</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Lowest Score</StatLabel>
                  <StatValue>{stats.lowest.toFixed(1)}%</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Highest Score</StatLabel>
                  <StatValue>{stats.highest.toFixed(1)}%</StatValue>
            </StatCard>
              </>
            )}
          </StatsContainer>
        );
      })()}

      {submissions.length > 0 && (
        <>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Search by student name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FilterContainer>
              <FilterLabel>
                <FiFilter size={16} />
                Status:
              </FilterLabel>
              <FilterButtons>
                <FilterButton
                  active={statusFilter === 'all'}
                  onClick={() => setStatusFilter('all')}
                >
                  All
                </FilterButton>
                <FilterButton
                  active={statusFilter === 'started'}
                  onClick={() => setStatusFilter('started')}
                >
                  Completed
                </FilterButton>
                <FilterButton
                  active={statusFilter === 'in_progress'}
                  onClick={() => setStatusFilter('in_progress')}
                >
                  In Progress
                </FilterButton>
                <FilterButton
                  active={statusFilter === 'not_started'}
                  onClick={() => setStatusFilter('not_started')}
                >
                  Not Started
                </FilterButton>
                <FilterButton
                  active={statusFilter === 'incomplete'}
                  onClick={() => setStatusFilter('incomplete')}
                  disabled={checkingIncomplete}
                >
                  {checkingIncomplete ? 'Checking...' : 'Incomplete'}
                </FilterButton>
              </FilterButtons>
              <FilterLabel style={{ marginLeft: '1.5rem' }}>
                Sort by Score:
              </FilterLabel>
              <FilterButtons>
                <FilterButton
                  active={sortOrder === 'none'}
                  onClick={() => setSortOrder('none')}
                >
                  None
                </FilterButton>
                <FilterButton
                  active={sortOrder === 'score-low-to-high'}
                  onClick={() => setSortOrder('score-low-to-high')}
                >
                  <FiArrowUp size={14} style={{ marginRight: '0.25rem' }} />
                  Low to High
                </FilterButton>
                <FilterButton
                  active={sortOrder === 'score-high-to-low'}
                  onClick={() => setSortOrder('score-high-to-low')}
                >
                  <FiArrowDown size={14} style={{ marginRight: '0.25rem' }} />
                  High to Low
                </FilterButton>
              </FilterButtons>
            </FilterContainer>
          </SearchContainer>
          <SearchResults style={{ marginBottom: '1rem', marginLeft: '1rem' }}>
            Showing {getFilteredSubmissions().length} of {submissions.length} submissions
          </SearchResults>
        </>
      )}

      {submissions.length > 0 ? (
        <SubmissionsList>
          {getFilteredSubmissions().map((submission) => (
            <SubmissionCard key={submission.assignment_id}>
              <SubmissionHeader>
                <StudentInfo>
                  <FiUser size={20} />
                  <div>
                    <StudentName>{submission.student_name || 'Unknown Student'}</StudentName>
                    <StudentEmail>{submission.student_email || 'No email'}</StudentEmail>
                  </div>
                </StudentInfo>
                <SubmissionMeta>
                  {submission.attempt_id ? (
                    <>
                      <ScoreDisplay correct={submission.score > 0}>
                        {submission.score || 0} / {submission.max_score || 0}
                        {submission.is_late && (
                          <span style={{ 
                            color: 'var(--warning-color)', 
                            marginLeft: '0.5rem', 
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            (Late)
                          </span>
                        )}
                      </ScoreDisplay>
                      <StatusBadge status={getStatusColor(submission)}>
                        {getStatusColor(submission) === 'completed' && <FiCheckCircle size={12} />}
                        {getStatusColor(submission) === 'in_progress' && <FiClock size={12} />}
                        {getStatusColor(submission)}
                      </StatusBadge>
                    </>
                  ) : (
                    <StatusBadge status="not_started">
                      <FiClock size={12} />
                      Not Started
                    </StatusBadge>
                  )}
                </SubmissionMeta>
              </SubmissionHeader>

              <SubmissionDetails>
                {submission.attempt_id ? (
                  <>
                    <DetailItem>
                      <FiCalendar size={14} />
                      Started: {formatDate(submission.started_at)}
                    </DetailItem>
                    <DetailItem>
                      <FiCheckCircle size={14} />
                      Submitted: {formatDate(submission.submitted_at)}
                    </DetailItem>
                    <DetailItem>
                      <FiClock size={14} />
                      Time Taken: {submission.time_taken ? `${Math.floor(submission.time_taken / 60)}m ${submission.time_taken % 60}s` : 'N/A'}
                    </DetailItem>
                    <DetailItem>
                      <FiUser size={14} />
                      Attempt: {submission.attempt_number || 1}
                    </DetailItem>
                  </>
                ) : (
                  <>
                    <DetailItem>
                      <FiCalendar size={14} />
                      Assigned: {formatDate(submission.assigned_at)}
                    </DetailItem>
                    <DetailItem>
                      <FiClock size={14} />
                      Due: {formatDate(submission.due_at)}
                    </DetailItem>
                    <DetailItem>
                      <FiUser size={14} />
                      Attempts Allowed: {submission.attempts_allowed}
                    </DetailItem>
                    <DetailItem>
                      <FiUser size={14} />
                      Attempts Used: {submission.attempts_used}
                    </DetailItem>
                  </>
                )}
              </SubmissionDetails>

              <Actions>
                {submission.attempt_id ? (
                  <>
                  <ActionButton 
                    className="primary"
                    onClick={() => handleViewSubmission(submission)}
                  >
                    <FiEye size={16} />
                    View Details
                  </ActionButton>
                    <ActionButton 
                      className="secondary"
                      onClick={() => handleResumeClick(submission)}
                    >
                      <FiPlay size={16} />
                      Resume Quiz
                    </ActionButton>
                    <ActionButton 
                      className="secondary"
                      onClick={() => handlePartialRetakeClick(submission)}
                      title="Allow student to retake specific questions"
                    >
                      <FiEdit3 size={16} />
                      Grant Partial Retake
                    </ActionButton>
                    {submission.started_at && !submission.submitted_at && (
                      <>
                        <ActionButton 
                          className="warning"
                          onClick={() => handleResetTimerClick(submission)}
                          title="Reset timer to full time limit"
                        >
                          <FiRotateCcw size={16} />
                          Reset Timer
                        </ActionButton>
                        <ActionButton 
                          className="secondary"
                          onClick={() => handleExtendTimerClick(submission)}
                          title="Add time to the timer"
                        >
                          <FiPlus size={16} />
                          Extend Timer
                        </ActionButton>
                      </>
                    )}
                    <ActionButton 
                      className="warning"
                      onClick={() => handleResetClick(submission)}
                    >
                      <FiRefreshCw size={16} />
                      Reset Quiz
                    </ActionButton>
                    <ActionButton 
                      className="danger"
                      onClick={() => handleUnassignClick(submission)}
                    >
                      <FiTrash2 size={16} />
                      Unassign Quiz
                    </ActionButton>
                  </>
                ) : (
                  <>
                  <ActionButton disabled>
                    <FiClock size={16} />
                      Not Started
                  </ActionButton>
                    <ActionButton 
                      className="danger"
                      onClick={() => handleUnassignClick(submission)}
                    >
                      <FiTrash2 size={16} />
                      Unassign Quiz
                    </ActionButton>
                  </>
                )}
              </Actions>
            </SubmissionCard>
          ))}
        </SubmissionsList>
      ) : (
        <EmptyState>
          <FiUser size={48} />
          <h3>No assignments yet</h3>
          <p>This quiz hasn't been assigned to any students yet.</p>
        </EmptyState>
      )}

      <ConfirmationModal
        isOpen={showResumeModal}
        onClose={() => {
          setShowResumeModal(false);
          setSelectedAssignment(null);
        }}
        onConfirm={handleConfirmResume}
        title="Resume Quiz Attempt"
        message={`Are you sure you want to enable resume for ${selectedAssignment?.student_name}?`}
        details="This will allow the student to continue/resume their quiz. All existing answers will be preserved. The quiz status will be set to 'in progress' and attempts_used will be reset to 0 for single-attempt quizzes."
        confirmText="Resume Quiz"
        cancelText="Cancel"
        isLoading={isResuming}
        type="info"
      />

      <ConfirmationModal
        isOpen={showResetModal}
        onClose={() => {
          setShowResetModal(false);
          setSelectedAssignment(null);
        }}
        onConfirm={handleConfirmReset}
        title="Reset Quiz Attempt"
        message={`Are you sure you want to reset the quiz attempt for ${selectedAssignment?.student_name}?`}
        details="This will clear all answers and reset the timer. The student will be able to start fresh as if it's their first attempt. This action cannot be undone."
        confirmText="Reset Quiz"
        cancelText="Cancel"
        isLoading={isResetting}
        type="warning"
      />

      <ConfirmationModal
        isOpen={showUnassignModal}
        onClose={() => {
          setShowUnassignModal(false);
          setSelectedAssignment(null);
        }}
        onConfirm={handleConfirmUnassign}
        title="Unassign Quiz"
        message={`Are you sure you want to unassign this quiz for ${selectedAssignment?.student_name}?`}
        details="This will permanently delete the quiz assignment and all associated attempts. This action cannot be undone."
        confirmText="Unassign Quiz"
        cancelText="Cancel"
        isLoading={isUnassigning}
        type="danger"
      />

      <ConfirmationModal
        isOpen={showResetTimerModal}
        onClose={() => {
          setShowResetTimerModal(false);
          setSelectedAssignment(null);
        }}
        onConfirm={handleConfirmResetTimer}
        title="Reset Timer"
        message={`Are you sure you want to reset the timer for ${selectedAssignment?.student_name}?`}
        details="This will reset the timer to the full time limit. The student's current progress and answers will be preserved, but they will have the full time remaining from now."
        confirmText="Reset Timer"
        cancelText="Cancel"
        isLoading={isResettingTimer}
        type="warning"
      />

      <ResumeTimerModal
        isOpen={showResumeTimerModal}
        onClose={() => {
          setShowResumeTimerModal(false);
          setSelectedAssignment(null);
          setTimeRemainingOnResume(null);
          setResumeExtendMinutes(0);
        }}
        onConfirm={handleConfirmResumeWithTimer}
        studentName={selectedAssignment?.student_name}
        timeRemaining={timeRemainingOnResume}
        timeLimitMinutes={selectedAssignment?.time_limit_minutes}
        extendMinutes={resumeExtendMinutes}
        onExtendMinutesChange={setResumeExtendMinutes}
        isLoading={isResuming}
      />

      <ExtendTimerModal
        isOpen={showExtendTimerModal}
        onClose={() => {
          setShowExtendTimerModal(false);
          setSelectedAssignment(null);
          setExtendMinutes(10);
        }}
        onConfirm={handleConfirmExtendTimer}
        studentName={selectedAssignment?.student_name}
        minutes={extendMinutes}
        onMinutesChange={setExtendMinutes}
        isLoading={isExtendingTimer}
      />

      <PartialRetakeModal
        isOpen={showPartialRetakeModal}
        onClose={() => {
          setShowPartialRetakeModal(false);
          setSelectedAssignment(null);
          setSelectedQuestionNumbers([]);
          setPartialRetakeTimerOption('keep');
          setPartialRetakeExtendMinutes(0);
        }}
        onConfirm={handleConfirmPartialRetake}
        studentName={selectedAssignment?.student_name}
        questions={quiz?.questions || []}
        timeRemaining={selectedAssignment?.time_limit_minutes && selectedAssignment?.started_at ? (() => {
          const timeLimitSeconds = selectedAssignment.time_limit_minutes * 60;
          const startedAt = new Date(selectedAssignment.started_at);
          const now = new Date();
          const elapsed = Math.floor((now - startedAt) / 1000);
          return Math.max(0, timeLimitSeconds - elapsed);
        })() : null}
        timeLimitMinutes={selectedAssignment?.time_limit_minutes}
        selectedQuestionNumbers={selectedQuestionNumbers}
        onQuestionNumbersChange={setSelectedQuestionNumbers}
        timerOption={partialRetakeTimerOption}
        onTimerOptionChange={setPartialRetakeTimerOption}
        extendMinutes={partialRetakeExtendMinutes}
        onExtendMinutesChange={setPartialRetakeExtendMinutes}
        attemptsAllowed={selectedAssignment?.attempts_allowed}
        isLoading={isGrantingPartialRetake}
        questionsWithResponses={questionsWithResponses}
      />

      <SuccessModal
        isOpen={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        title="Success"
        message={successMessage}
      />

      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        onConfirm={() => setShowErrorModal(false)}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText={null}
        type="danger"
      />
    </Container>
  );
}

export default QuizSubmissions;
